import type { ConnectorProvider } from '@hvac/contracts';
import { cn } from '@hvac/ui';

/**
 * A system's mark: a coloured tile with its initials. Not the companies'
 * logos, which are theirs; enough colour that "the green one" and "the blue
 * one" are told apart at a glance across a page of tiles.
 */

type MarkKind = ConnectorProvider | 'spreadsheet';

const MARKS: Readonly<Record<MarkKind, { letters: string; className: string }>> = {
  housecall_pro: { letters: 'HP', className: 'from-sky-500 to-blue-700' },
  jobber: { letters: 'J', className: 'from-emerald-500 to-green-700' },
  quickbooks_online: { letters: 'QB', className: 'from-lime-500 to-emerald-700' },
  servicetitan: { letters: 'ST', className: 'from-slate-600 to-slate-900' },
  spreadsheet: { letters: '', className: 'from-amber-400 to-orange-600' },
};

export function ConnectorMark({
  kind,
  size = 'md',
  className,
}: {
  kind: MarkKind;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const mark = MARKS[kind];
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center bg-gradient-to-br font-bold tracking-tight text-white',
        'shadow-[inset_0_1px_0_rgb(255_255_255/0.25),0_1px_2px_rgb(0_0_0/0.12)]',
        size === 'sm' && 'size-8 rounded-[9px] text-xs',
        size === 'md' && 'size-11 rounded-[12px] text-sm',
        size === 'lg' && 'size-14 rounded-[14px] text-base',
        mark.className,
        className,
      )}
    >
      {kind === 'spreadsheet' ? <SheetGlyph /> : mark.letters}
    </span>
  );
}

function SheetGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="size-1/2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18" />
    </svg>
  );
}
